import { useState } from 'react';
import OpenAI from "openai";
import AppIcon from '../component/AppIcon';

const API_KEY = process.env.REACT_APP_OPENAI_API_KEY;

const openai = new OpenAI({apiKey: API_KEY, dangerouslyAllowBrowser: true});

const Chat = () => {
    const [message, setMessage] = useState('');
    const [chatHistory, setChatHistory] = useState([
        { role: 'assistant', content: 'Welcome to AI Chat! How can I help you today?' }
    ]);
    const [isLoading, setIsLoading] = useState(false);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        
        if (!message.trim()) return;

        // Add user message to chat
        setChatHistory([...chatHistory, { role: 'user', content: message }]);
        
        // Clear input
        setMessage('');
        setIsLoading(true);
        
        try {
            const response = await openai.chat.completions.create({
                model: "gpt-4.1",
                messages: [
                    { role: "system", content: "You are a helpful assistant." },
                    ...chatHistory.map(msg => ({ role: msg.role, content: msg.content })),
                    { role: "user", content: message }
                ],
                temperature: 0.7,
                max_completion_tokens: 2048,
            });
            
            setChatHistory(prev => [
                ...prev, 
                { 
                    role: 'assistant', 
                    content: response.choices[0].message.content
                }
            ]);
        } catch (error) {
            console.error('Error:', error);
            setChatHistory(prev => [
                ...prev, 
                { 
                    role: 'assistant', 
                    content: "I'm sorry, I encountered an error. Please try again later."
                }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto h-[calc(100vh-12rem)] flex flex-col animate-fadeIn">
            <div className="card dark:bg-gray-800 mb-4">
                <div className="flex items-center">
                    <AppIcon className="h-10 w-10 mr-3" />
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-1">AI Chat</h1>
                        <p className="text-gray-600 dark:text-gray-300">Chat with our AI assistant</p>
                    </div>
                </div>
            </div>
            
            <div className="flex-1 overflow-y-auto mb-4 card dark:bg-gray-800">
                <div className="space-y-4 p-4">
                    {chatHistory.map((msg, index) => (
                        <div 
                            key={index} 
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-slideIn`}
                            style={{ animationDelay: `${index * 100}ms` }}
                        >
                            <div 
                                className={`max-w-[80%] rounded-lg px-4 py-2 ${
                                    msg.role === 'user' 
                                        ? 'bg-blue-500 text-white' 
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                }`}
                            >
                                {msg.content}
                            </div>
                        </div>
                    ))}
                    
                    {isLoading && (
                        <div className="flex justify-start">
                            <div className="bg-gray-200 dark:bg-gray-700 rounded-lg px-4 py-2 flex items-center space-x-1">
                                <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-pulse"></div>
                                <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                                <div className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            <form onSubmit={handleSendMessage} className="flex gap-2">
                <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="input flex-1"
                    disabled={isLoading}
                />
                <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={!message.trim() || isLoading}
                >
                    {isLoading ? (
                        <span className="inline-block h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                        </svg>
                    )}
                </button>
            </form>
        </div>
    );
};

export default Chat;